import { CheckCircle, X, Tag, Package, Info } from 'lucide-react';
import type { SectionVariant } from './VariantsList';

interface SectionPreviewProps {
  section: {
    id: string;
    label: string;
    description?: string;
    allowedSlots: string[];
    variants: SectionVariant[] | string[];
  };
  availableSlots: string[];
  className?: string;
}

export function SectionPreview({
  section,
  availableSlots,
  className = '',
}: SectionPreviewProps) {
  // Normalize variants to object format
  const normalizedVariants: SectionVariant[] = section.variants.map((v) =>
    typeof v === 'string' ? { id: v, label: v } : v
  );

  return (
    <div className={`bg-gray-50 rounded-lg border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white rounded-t-lg">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <Package className="w-5 h-5 text-primary-600" />
              <h3 className="text-base font-semibold text-gray-900">
                {section.label || section.id}
              </h3>
            </div>
            <p className="text-xs text-gray-500 font-mono mt-1">
              ID: {section.id}
            </p>
          </div>
          <div className="ml-4">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Anteprima
            </span>
          </div>
        </div>
        {section.description && (
          <p className="text-sm text-gray-600 mt-2">
            {section.description}
          </p>
        )}
      </div>

      {/* Body */}
      <div className="p-4 space-y-4">
        {/* Allowed Slots Section */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <Tag className="w-4 h-4 text-gray-500" />
            <h4 className="text-sm font-medium text-gray-700">
              Slot Consentiti
            </h4>
            <span className="text-xs text-gray-500">
              ({section.allowedSlots.length} di {availableSlots.length})
            </span>
          </div>

          {section.allowedSlots.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start space-x-2">
              <Info className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">
                Nessuno slot selezionato. Seleziona almeno uno slot per continuare.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {availableSlots.map((slot) => {
                const isAllowed = section.allowedSlots.includes(slot);
                return (
                  <div
                    key={slot}
                    className={`flex items-center justify-between px-3 py-2 rounded-md border transition-colors ${
                      isAllowed
                        ? 'bg-green-50 border-green-200'
                        : 'bg-gray-50 border-gray-200 opacity-40'
                    }`}
                  >
                    <div className="flex items-center space-x-2">
                      {isAllowed ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <X className="w-4 h-4 text-gray-400" />
                      )}
                      <span
                        className={`text-sm font-mono ${
                          isAllowed ? 'text-green-900 font-medium' : 'text-gray-500'
                        }`}
                      >
                        {slot}
                      </span>
                    </div>
                    {isAllowed && (
                      <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded">
                        Consentito
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Variants Section */}
        <div>
          <div className="flex items-center space-x-2 mb-3">
            <Tag className="w-4 h-4 text-gray-500" />
            <h4 className="text-sm font-medium text-gray-700">
              Varianti
            </h4>
            <span className="text-xs text-gray-500">
              ({normalizedVariants.length})
            </span>
          </div>

          {normalizedVariants.length === 0 ? (
            <div className="bg-gray-100 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500 italic">
                Nessuna variante definita
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {normalizedVariants.map((variant, index) => {
                // Determine variant status badge
                const status = variant.status || 'active';
                const statusConfig = {
                  active: {
                    bg: 'bg-green-100',
                    text: 'text-green-800',
                    label: 'Attiva',
                  },
                  planned: {
                    bg: 'bg-blue-100',
                    text: 'text-blue-800',
                    label: 'Pianificata',
                  },
                  deprecated: {
                    bg: 'bg-orange-100',
                    text: 'text-orange-800',
                    label: 'Deprecata',
                  },
                };

                const config = statusConfig[status] || statusConfig.active;

                return (
                  <div
                    key={variant.id || index}
                    className="flex items-center justify-between px-3 py-2 bg-white rounded-md border border-gray-200"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-900">
                          {variant.label || variant.id}
                        </span>
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}
                        >
                          {config.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 font-mono mt-0.5">
                        ID: {variant.id}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Metadata Section */}
        <div className="pt-3 border-t border-gray-200">
          <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            Metadata
          </h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="bg-white rounded px-2 py-1.5 border border-gray-200">
              <span className="text-gray-500">Allowed Slots:</span>{' '}
              <span className="font-medium text-gray-900">
                {section.allowedSlots.length}
              </span>
            </div>
            <div className="bg-white rounded px-2 py-1.5 border border-gray-200">
              <span className="text-gray-500">Varianti:</span>{' '}
              <span className="font-medium text-gray-900">
                {normalizedVariants.length}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
